/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	ContentLargeImageSection,
	CosmosComponentContextProps,
	DividerSection,
	getHero,
	heroOwnsHeading,
	getSections,
	LicenseCardsSection,
	LicenseSeatData,
} from "@cosmos/components";
import { CosmosPageLicenseFragment } from "@cosmos/hygraph-schema";

import { css } from "@panda/css";

import { TemplateProviders } from "../templates";

interface Props {
	pageData?: CosmosPageLicenseFragment;
	componentContext?: CosmosComponentContextProps;
	locale: string;
	seatsData1?: LicenseSeatData;
	seatsData2?: LicenseSeatData;
}

export const CosmosPageLicenseClient = ({
	pageData,
	locale,
	componentContext,
	seatsData1,
	seatsData2,
}: Props): React.JSX.Element => {
	// Only a hero that renders an h1 owns it; a decorative hero (e.g. a glow with no title)
	// must not suppress the first section's promotion — gate on heroOwnsHeading, not presence.
	const hero = getHero(pageData?.hero);

	return (
		<TemplateProviders componentContext={componentContext}>
			{hero}

			{pageData?.contentLargeImages && pageData?.contentLargeImages?.length > 0 ? (
				<div className={css({ paddingTop: "eds.dimension.96", xlDown: { paddingTop: "eds.dimension.80" } })}>
					<DividerSection />
					{pageData?.contentLargeImages?.map((section) => (
						<ContentLargeImageSection key={section.id} {...section} />
					))}
					<DividerSection />
				</div>
			) : null}

			<LicenseCardsSection pageData={pageData} locale={locale} seatsData1={seatsData1} seatsData2={seatsData2} />

			{getSections(pageData?.refSections, undefined, heroOwnsHeading(pageData?.hero))}
		</TemplateProviders>
	);
};
